/**
 * Šifrování zpráv v prohlížeči (end-to-end).
 *
 * Cíl: server dostane jen šifru, kterou nedokáže přečíst. Ani kdyby ji někdo
 * vyžádal soudně.
 *
 * Jak to funguje:
 *   1. Každý člen má pár klíčů (ECDH P-256). Veřejný klíč leží na serveru,
 *      soukromý taky – ale zašifrovaný heslem, takže je serveru k ničemu.
 *   2. Klíč, kterým se soukromý klíč zamyká, vznikne z hesla (PBKDF2) přímo
 *      v prohlížeči a nikdy nikam neodejde.
 *   3. Klíč pro konverzaci si obě strany spočítají samy ze svého soukromého
 *      a cizího veřejného klíče (ECDH). Server ho nikdy nevidí, protože ho
 *      nikdo neposílá.
 *   4. Zpráva se šifruje AES-256-GCM s náhodným IV.
 *
 * Používá se jen Web Crypto, který je v prohlížeči zabudovaný. Žádná knihovna
 * navíc – u kryptografie je každá další závislost místo, kde se dá něco
 * podstrčit.
 *
 * Co tohle NEŘEŠÍ, ať to není napsané jen v dokumentaci:
 *   – metadata: server ví, kdo komu a kdy psal, protože zprávu musí doručit,
 *   – dopřednou bezpečnost: prozrazení hesla otevře i staré zprávy
 *     (na to je potřeba ratchet, což je další stavba),
 *   – to, že web si kód stahuje ze serveru. Kdo ovládne server, může
 *     podstrčit upravený kód. Proti tomu pomůže jen podepsaná aplikace.
 */

const enc = new TextEncoder();
const dec = new TextDecoder();

// Kolik kol PBKDF2. Doporučení OWASP pro PBKDF2-SHA256; na běžném telefonu
// to trvá kolem sekundy, což je při přihlášení únosné a útočníkovi to
// hádání hesel pořádně prodraží.
export const PBKDF2_KOL = 600000;

export const jePodporovano = () =>
  typeof window !== 'undefined'
  && window.crypto?.subtle
  && typeof window.crypto.subtle.deriveKey === 'function';

// ── Převody ───────────────────────────────────────────────────────────────────

export const doBase64 = (buf) => {
  const bajty = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bajty.length; i += 1) s += String.fromCharCode(bajty[i]);
  return btoa(s);
};

export const zBase64 = (text) => {
  const s = atob(text);
  const bajty = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i += 1) bajty[i] = s.charCodeAt(i);
  return bajty;
};

const nahodne = (delka) => window.crypto.getRandomValues(new Uint8Array(delka));

// ── Klíč z hesla ──────────────────────────────────────────────────────────────

/**
 * Z hesla udělá klíč, kterým se zamyká soukromý klíč.
 *
 * Sůl je u každého člena jiná a leží na serveru – sama o sobě nic neprozradí,
 * jen brání tomu, aby útočník předpočítal tabulky pro všechny najednou.
 */
async function klicZHesla(heslo, sul, kol = PBKDF2_KOL) {
  const zaklad = await window.crypto.subtle.importKey(
    'raw', enc.encode(heslo), 'PBKDF2', false, ['deriveKey'],
  );
  return window.crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: sul, iterations: kol, hash: 'SHA-256' },
    zaklad,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

// ── Pár klíčů člena ───────────────────────────────────────────────────────────

/**
 * Vyrobí nový pár klíčů a soukromý rovnou zamkne heslem.
 *
 * Vrací balíček, který se pošle na server. Soukromý klíč v něm je zašifrovaný;
 * server ho uloží, ale nepřečte.
 */
export async function vytvorBalicekKlicu(heslo) {
  const par = await window.crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveKey', 'deriveBits'],
  );

  const verejny = await window.crypto.subtle.exportKey('spki', par.publicKey);
  const soukromy = await window.crypto.subtle.exportKey('pkcs8', par.privateKey);

  const sul = nahodne(16);
  const iv = nahodne(12);
  const zamek = await klicZHesla(heslo, sul);
  const zasifrovany = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, zamek, soukromy,
  );

  return {
    public_key: doBase64(verejny),
    encrypted_private_key: doBase64(zasifrovany),
    key_salt: doBase64(sul),
    key_iv: doBase64(iv),
    key_iterations: PBKDF2_KOL,
    key_algo: 'ECDH-P256/AES-GCM/PBKDF2-SHA256',
  };
}

/**
 * Rozšifruje uložený soukromý klíč na holé bajty.
 *
 * Vnitřní pomocník. Hotový klíč se pak importuje jako neexportovatelný, takže
 * ho žádný jiný kód na stránce nedostane ven – ani kdyby se tam nějaký dostal.
 * Právě proto se přebalení novým heslem dělá tady z bajtů a ne z hotového
 * klíče: ten už by exportovat nešel.
 */
async function odemkniSuroveBajty(balicek, heslo) {
  try {
    const zamek = await klicZHesla(
      heslo, zBase64(balicek.key_salt), balicek.key_iterations || PBKDF2_KOL,
    );
    return await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: zBase64(balicek.key_iv) },
      zamek,
      zBase64(balicek.encrypted_private_key),
    );
  } catch {
    return null;
  }
}

/**
 * Odemkne soukromý klíč heslem. Vrací `null`, když heslo nesedí –
 * nerozlišujeme mezi „špatné heslo" a „poškozená data", protože z hlediska
 * uživatele je to totéž a rozlišování by jen napovídalo útočníkovi.
 */
export async function odemkniSoukromyKlic(balicek, heslo) {
  const surovy = await odemkniSuroveBajty(balicek, heslo);
  if (!surovy) return null;
  try {
    return await window.crypto.subtle.importKey(
      'pkcs8', surovy, { name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveKey', 'deriveBits'],
    );
  } catch {
    return null;
  }
}

/**
 * Přebalí soukromý klíč novým heslem – po změně hesla.
 *
 * Veřejný klíč zůstává stejný, takže staré zprávy zůstanou čitelné. Měnil by se
 * jen tehdy, kdyby si člověk vyrobil úplně novou totožnost, a to je jiná věc.
 */
export async function prebalNovymHeslem(balicek, stareHeslo, noveHeslo) {
  const surovy = await odemkniSuroveBajty(balicek, stareHeslo);
  if (!surovy) return null;

  const sul = nahodne(16);
  const iv = nahodne(12);
  const zamek = await klicZHesla(noveHeslo, sul);
  const zasifrovany = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, zamek, surovy);

  return {
    ...balicek,
    encrypted_private_key: doBase64(zasifrovany),
    key_salt: doBase64(sul),
    key_iv: doBase64(iv),
    key_iterations: PBKDF2_KOL,
  };
}

// ── Klíč konverzace ───────────────────────────────────────────────────────────

/**
 * Spočítá klíč pro konverzaci s druhým členem.
 *
 * Obě strany dojdou ke stejnému klíči, aniž by si ho poslaly – to je celé
 * kouzlo ECDH. Server ho tedy nemá odkud vzít.
 */
export async function klicKonverzace(mujSoukromy, cizVerejnyBase64) {
  const cizi = await window.crypto.subtle.importKey(
    'spki', zBase64(cizVerejnyBase64), { name: 'ECDH', namedCurve: 'P-256' }, false, [],
  );
  return window.crypto.subtle.deriveKey(
    { name: 'ECDH', public: cizi },
    mujSoukromy,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

// ── Šifrování obsahu ──────────────────────────────────────────────────────────

/** Zašifruje text. Výsledek je jeden řetězec, který jde uložit místo zprávy. */
export async function zasifrujText(klic, text) {
  const iv = nahodne(12);
  const sifra = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, klic, enc.encode(text),
  );
  // Předpona říká, čím je to zašifrované. Až se metoda někdy změní, půjde
  // poznat, co je čím – bez toho by se staré zprávy nedaly přečíst.
  return `e2e1:${doBase64(iv)}:${doBase64(sifra)}`;
}

/** Rozšifruje text. Když to nejde, vrátí `null` – volající rozhodne, co s tím. */
export async function desifrujText(klic, ulozene) {
  if (typeof ulozene !== 'string' || !ulozene.startsWith('e2e1:')) return null;
  try {
    const [, ivB64, sifraB64] = ulozene.split(':');
    const otevrene = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: zBase64(ivB64) }, klic, zBase64(sifraB64),
    );
    return dec.decode(otevrene);
  } catch {
    return null;
  }
}

/** Zašifruje soubor (fotku, hlasovku). Vrací data k odeslání. */
export async function zasifrujSoubor(klic, soubor) {
  const obsah = await soubor.arrayBuffer();
  const iv = nahodne(12);
  const sifra = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, klic, obsah);
  return {
    data: new Blob([sifra], { type: 'application/octet-stream' }),
    iv: doBase64(iv),
    // Původní typ a název jdou zvlášť, protože samotná data už jsou nečitelná.
    puvodni_typ: soubor.type || 'application/octet-stream',
    puvodni_nazev: soubor.name || 'priloha',
  };
}

export async function desifrujSoubor(klic, sifraBuffer, ivBase64, typ) {
  try {
    const otevrene = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: zBase64(ivBase64) }, klic, sifraBuffer,
    );
    return new Blob([otevrene], { type: typ || 'application/octet-stream' });
  } catch {
    return null;
  }
}

/** Poznávací značka – podle ní se pozná, co je šifrované koncově a co ne. */
export const jeE2E = (text) => typeof text === 'string' && text.startsWith('e2e1:');

// ── Otisk hesla pro přihlášení ────────────────────────────────────────────────

/**
 * Z hesla udělá otisk, kterým se člověk přihlašuje.
 *
 * Server tak heslo nikdy nedostane. Je to důležité kvůli šifrování: klíč ke
 * zprávám se odemyká heslem, takže dokud server heslo vidí, mohl by z něj klíč
 * odvodit. S otiskem to nejde.
 *
 * Otisk a klíč ke zprávám vznikají z téhož hesla, ale jinou solí a jinou
 * předponou. Ze znalosti jednoho se druhý spočítat nedá.
 *
 * Sůl je u každého jiná a leží na serveru – sama o sobě nic neprozradí.
 */
export async function otiskHesla(heslo, sulBase64, kol = PBKDF2_KOL) {
  const zaklad = await window.crypto.subtle.importKey(
    'raw', enc.encode(`bloom-prihlaseni|${heslo}`), 'PBKDF2', false, ['deriveBits'],
  );
  const bity = await window.crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: zBase64(sulBase64), iterations: kol, hash: 'SHA-256' },
    zaklad,
    256,
  );
  return doBase64(bity);
}

/** Nová sůl pro přihlašovací otisk – při registraci a při změně hesla. */
export const novaSulProPrihlaseni = () => doBase64(nahodne(16));
